import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useEffect } from "react";
import {
  ferrySearchFormUpdate,
  ferrySearchResultsFailed,
  ferrySearchResultsIdle,
  ferrySearchResultsLoading,
  ferrySearchResultsUpdate,
  selectedFerryFailed,
  selectedFerryIdle,
  selectedFerryLoading,
  selectedFerryUpdate,
} from "../../actions/search/ferries_search";
import { retrieveSearchSessionMetadata, storeSearchSessionMetadata } from "../../api";
import { ferriesErrorMsgs } from "../../errors";
import { useHistory } from "react-router-dom";
import { DateTime } from "luxon";
import { pollFerriesSearch, retrieveFerryDetails, searchRequestFerries } from "../../api/ferries";
import { changeServiceSessionIds } from "../../actions/search/session_ids";
import { useApoAuth, useQueryParams } from "..";

function actionFailed({ history, onFailed, errorMsg }) {
  onFailed();
  toast.warning(errorMsg);
  history.push("/");
}

function getFerrySearchPayload({ form, market_type, source_entity = "B2C" }) {
  var originLeg = {};
  var returnLeg = {};
  var legs = [];

  originLeg = {
    origin: form.origin.code,
    destination: form.destination.code,
    is_return_leg: false,
    departure_datetime: DateTime.fromISO(form.departDateTime)
      .setZone("UTC", {
        keepLocalTime: true,
      })
      .toISO(),

    vehicles: [],
  };
  legs.push(originLeg);

  if (form.isRoundtrip && form.returnDateTime) {
    returnLeg = {
      origin: form.destination.code,
      departure_datetime: DateTime.fromISO(form.returnDateTime)
        .setZone("UTC", {
          keepLocalTime: true,
        })
        .toISO(),
      destination: form.origin.code,
      vehicles: [],

      is_return_leg: true,
    };
    legs.push(returnLeg);
  }

  var output = {
    language: "en-GB", // TODO: To be fixed if other languages are supported
    market_type,
    source_entity,
    currency: form.currency,
    pax: {
      adults: form.adults,
      seniors: form.seniors,
      children: form.children,
      children_ages: form.children_ages,
    },
    legs: [...legs],
  };

  return output;
}

export function useFerriesInitSearch({ setLoadingSearchBtn = () => {} } = {}) {
  const dispatch = useDispatch();
  const history = useHistory();

  const search_form = useSelector((state) => state.FerrySearchForm);
  const { source_entity, market_type } = useApoAuth();

  const onFormUpdate = useCallback((form) => dispatch(ferrySearchFormUpdate({ form })), [dispatch]);

  const onChangeSessionIDs = useCallback(
    (value) => dispatch(changeServiceSessionIds("FER", value)),
    [dispatch],
  );

  const onSearchResultsLoading = useCallback(
    () => dispatch(ferrySearchResultsLoading()),
    [dispatch],
  );

  const onFailed = useCallback(() => {
    dispatch(ferrySearchResultsIdle());
    dispatch(ferrySearchResultsFailed());
  }, [dispatch]);

  async function initSearch(form) {
    if (_.isEmpty(form)) return;
    const fullForm = { ...search_form, ...form };
    onSearchResultsLoading();

    const payload = getFerrySearchPayload({
      form: fullForm,
      market_type,
      source_entity,
    });
    onFormUpdate({ ...fullForm, searchPayload: payload });

    const results = await searchRequestFerries(payload);

    if (!results || results.status !== "searching" || !results.session_id) {
      if (typeof setLoadingSearchBtn === "function") {
        setLoadingSearchBtn(false);
      }
      actionFailed({
        history,
        onFailed,
        errorMsg: ferriesErrorMsgs.searchForm.init_search,
      });
      return;
    }
    const search_session_id = _.get(results, "session_id", "");

    const searchSessionMetaResult = await storeSearchSessionMetadata({
      service_type: "FER",
      session_data: {
        search_session_id,
        search_form: { ...fullForm, searchPayload: payload },
      },
    });
    if (
      _.isEmpty(_.get(searchSessionMetaResult, "session_data", {})) ||
      !searchSessionMetaResult.session_id
    ) {
      if (typeof setLoadingSearchBtn === "function") {
        setLoadingSearchBtn(false);
      }
      actionFailed({
        history,
        onFailed,
        errorMsg: ferriesErrorMsgs.searchForm.no_session,
      });

      return;
    }
    const { session_id: page_session_id } = searchSessionMetaResult;

    onChangeSessionIDs({ page_session_id, search_session_id });

    history.push(`/ferries/${page_session_id}`);
  }

  return [initSearch];
}

/**
 * Hook that checks redux store for search session data and
 * if it exists, it loads it into the store.
 * Else, it makes a poll request to the backend to get the
 * search session data.
 * @param {*} dispatch, history, page_session_id
 */
export function useFerriesGetSessionMeta({ page_session_id }) {
  const dispatch = useDispatch();
  const history = useHistory();

  const { allFerriesLength, ferrySelected, search_session_id } = useSelector((state) => {
    return {
      allFerriesLength: _.get(state.FerrySearchResults.ferries, "length", 0),
      ferrySelected: state.FerrySelected,
      search_session_id: state.SessionIds.FER.search_session_id,
    };
  });

  const [loadingMeta, setLoadingMeta] = useState(search_session_id ? false : true);

  const onFormUpdate = useCallback((form) => dispatch(ferrySearchFormUpdate({ form })), [dispatch]);

  const onChangeSessionIDs = useCallback(
    (value) => dispatch(changeServiceSessionIds("FER", value)),
    [dispatch],
  );

  const onFailed = useCallback(() => {
    dispatch(ferrySearchResultsIdle());
    dispatch(ferrySearchResultsFailed());
  }, [dispatch]);

  const pageSessionFetcher = useCallback(
    ({ page_session_id }) => {
      retrieveSearchSessionMetadata({ page_session_id })
        .then(async (data) => {
          if (!data) {
            actionFailed({
              history,
              onFailed,
              errorMsg: ferriesErrorMsgs.searchForm.no_session,
            });
            return;
          }

          const form = _.get(data, "session_data.data.search_form", {});

          onChangeSessionIDs({
            page_session_id,
            search_session_id: _.get(data, "session_data.data.search_session_id", ""),
          });

          await onFormUpdate(form);
          setLoadingMeta(false);
        })
        .catch(() => {
          actionFailed({
            history,
            onFailed,
            errorMsg: ferriesErrorMsgs.searchForm.no_session,
          });
        });
    },
    [dispatch],
  );

  useEffect(() => {
    if (allFerriesLength && _.isEmpty(ferrySelected)) {
      return;
    }
    if (!page_session_id) {
      actionFailed({
        history,
        onFailed,
        errorMsg: ferriesErrorMsgs.searchForm.no_session,
      });
      return;
    }
    if (!search_session_id) {
      setLoadingMeta(true);
      pageSessionFetcher({ page_session_id });
      return;
    }
  }, [search_session_id, page_session_id]);

  return { search_session_id, loadingMeta };
}

export function useFerriesPoller({ search_session_id }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();

  // redux actions
  const onSearchResultsLoading = useCallback(
    () => dispatch(ferrySearchResultsLoading()),
    [dispatch],
  );

  const onSearchResultsUpdate = useCallback(
    (data) => dispatch(ferrySearchResultsUpdate(data)),
    [dispatch],
  );

  const onFailed = useCallback(() => {
    dispatch(ferrySearchResultsIdle());
    dispatch(ferrySearchResultsFailed());
  }, [dispatch]);

  const maxPollCount = 7;

  function rePoller({ pollCount }) {
    setTimeout(() => poller({ pollCount }), 3000);
  }

  async function poller({ pollCount = 0 }) {
    const poll_response = await pollFerriesSearch({
      session_id: search_session_id,
      onError: (error) => {
        if (error.response.status === 400) {
          return { results: [], status: "working", error: { status: null } };
        }
        return {
          error: {
            status: error.response.status,
            message: error.response.data.message,
          },
        };
      },
    });
    if (poll_response.error?.status || pollCount >= maxPollCount) {
      actionFailed({
        history,
        onFailed,
        errorMsg: poll_response.error?.status
          ? poll_response.error.message
          : ferriesErrorMsgs.general.unexpectedError,
      });
      return;
    }

    const is_done = _.get(poll_response, "status") === "done";
    const results = _.get(poll_response, "results", []);
    const has_results = results.length > 0;

    if (is_done && !has_results) {
      actionFailed({
        history,
        onFailed,
        errorMsg: ferriesErrorMsgs.resultsList.no_results,
      });
      return;
    }

    if (has_results) {
      onSearchResultsUpdate({ ferries: results, loading: false });
    }

    if (!is_done && pollCount < maxPollCount) {
      rePoller({ pollCount: pollCount + 1 });
    }
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    if (!search_session_id || !loading) {
      return;
    }

    onSearchResultsLoading();

    poller({ pollCount: 0 });
  }, [search_session_id, loading]);
}

export const useGetFerryDetails = ({ search_session_id, token, provider }) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();

  const onFerrySelectedLoading = useCallback(() => dispatch(selectedFerryLoading()), [dispatch]);

  const onSelectedFerryUpdate = useCallback(
    (data) => {
      dispatch(selectedFerryUpdate(data));
      dispatch(selectedFerryIdle());
    },
    [dispatch],
  );

  const onFailed = useCallback(() => {
    dispatch(selectedFerryIdle());
    dispatch(selectedFerryFailed());
  }, [dispatch]);

  async function getFerryDetails() {
    const result = await retrieveFerryDetails({
      payload: { session_id: search_session_id, token, provider },
      onError: (error) => ({
        error: {
          status: error.response.status,
          message: error.response.data.message,
        },
      }),
    });

    if (result.error) {
      actionFailed({
        history,
        onFailed,
        errorMsg: result.error.status
          ? result.error.message
          : ferriesErrorMsgs.general.unexpectedError,
      });
      return;
    }

    onSelectedFerryUpdate({ ...result, loading: false });
  }

  useEffect(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    if (!search_session_id || !loading) {
      return;
    }
    onFerrySelectedLoading();
    getFerryDetails();
  }, [search_session_id, loading]);
};

export function useGetFerQueryParams() {
  const history = useHistory();
  var params = useQueryParams();
  var selectionData = params.get("data");

  try {
    selectionData = window.atob(selectionData);
    selectionData = JSON.parse(selectionData);
    return { selectionData };
  } catch (error) {
    history.push("/404/");
    return { selectionData: {} };
  }
}
